import { Component, ElementRef, HostListener, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AppointmentStatus, Complaint, ComplaintAppointment, ComplaintPosition, ComplaintUserType, DocumentAttachment, ProtocolEmailStatus, Synced } from '@icc/b2b/shared';
import { DIALOG_DATA, DialogRef } from '@icc/ui';
import SignaturePad from 'signature_pad';
import { BreakpointObserver } from '@angular/cdk/layout';
import { filter, fromEvent, map, Subject, Subscription, switchMap, take } from 'rxjs';
import { ComplaintService } from '../complaint.service';
import { ComplaintAppointmentService } from '../complaint-appointment.service';
import { SyncService } from '@icc/b2b/sales-document';
import { ToastService } from '@icc/shared/helpers';
import { TranslateService } from '@ngx-translate/core';
import { ConfigService } from '@icc/shared';

@Component({
    selector: 'icc-complaint-appointment-protocol-dialog',
    templateUrl: './complaint-appointment-protocol-dialog.component.html',
    styleUrls: ['./complaint-appointment-protocol-dialog.component.scss'],
})
export class ComplaintAppointmentProtocolDialogComponent implements OnInit, OnDestroy {
    signMode = false;
    saving = false;
    clientSigned = false;
    servicemanSigned = false;
    withoutClientSign = false;

    subscriptions: Subscription[] = [];
    mobileView = false;
    isHorizontal = false;

    @ViewChild('signaturePad', { static: false })
    signaturePadElement: ElementRef<HTMLCanvasElement>;
    private signaturePad: SignaturePad;

    @HostListener('window:resize', ['$event'])
    onResize(event: Event) {
        setTimeout(() => {
            this.checkOrientation();
        }, 50);
    }

    @HostListener('window:orientationchange', ['$event'])
    onOrientationChange(event: Event) {
        setTimeout(() => {
            this.checkOrientation();
        }, 50);
    }

    synced: Synced = Synced.Syncing;

    isProcessingPdf = false;
    activeUserCompanyId: number;

    authorizedRetailerDistributorComplaint = false;
    distributorManufacturerComplaint = false;
    sign$ = new Subject<void>();
    syncedAfterSign$ = this.sign$.pipe(
        switchMap(() => this.syncService.synced$),
        map((synced) => this.syncService.getSynced([this.data.appointment.documentId], synced.synced, true)),
        filter((synced) => synced === Synced.Synced && !!this.data.appointment.servicemanSignatureImage)
    );

    constructor(
        private complaintService: ComplaintService,
        private complaintAppointmentService: ComplaintAppointmentService,
        private breakpointObserver: BreakpointObserver,
        private dialogRef: DialogRef,
        @Inject(DIALOG_DATA)
        public data: {
            appointment: ComplaintAppointment;
            attachments: DocumentAttachment[];
            complaint: Complaint;
            editMode: boolean;
            clientView: boolean;
            positionsWorkflowsGroups: Map<number, ComplaintPosition[]>;
            userType: ComplaintUserType;
        },
        private syncService: SyncService,
        private toastService: ToastService,
        private translateService: TranslateService,
        private configService: ConfigService,
    ) {
        this.activeUserCompanyId = Number(this.configService?.user?.user?.ActiveCompany);
        this.authorizedRetailerDistributorComplaint = this.data.complaint.clientName.length < 1 && this.data.complaint.authorizedRetailerCompanyId !== null;
        this.distributorManufacturerComplaint = this.data.complaint.clientName.length < 1 && this.data.complaint.authorizedRetailerCompanyId === null;

        if (window.screen.orientation) {
            window.screen.orientation.addEventListener('change', () => {
                setTimeout(() => {
                    this.checkOrientation();
                }, 50);
            });
        }

    }

    ngOnInit(): void {
        if (!this.data.appointment.servicemanSignatureImage) {
            this.retrieveFormDataFromLocalStorage();
        }
        this.subscriptions.push(
            this.breakpointObserver.observe(['(max-width: 990px)']).subscribe((result) => {
                this.mobileView = result.matches;
            }),
            this.syncService.synced$.subscribe((synced) => {
                this.synced = this.syncService.getSynced([this.data.appointment.documentId], synced.synced, true);
            }),
            this.complaintService.isProcessingPdf$.subscribe(value => {
                this.isProcessingPdf = value;
            }),
            this.syncService.protocolEmailStatus$.subscribe(this.handleEmailStatusUpdate.bind(this)),
            this.syncedAfterSign$.pipe(take(1)).subscribe(() => {
                this.triggerWorkflowAfterSignSynced();
            }),
        );

        if (window.screen.orientation) {
            this.subscriptions.push(
                fromEvent(window.screen.orientation, 'change').subscribe(() => {
                    setTimeout(() => {
                        this.checkOrientation();
                    }, 50);
                })
            );
        }

        this.checkOrientation();
        if (!this.data.appointment.servicemanSignatureImage) {
            this.loadAttachments();
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach((s) => s.unsubscribe());
        if (!this.data.appointment.servicemanSignatureImage) {
            this.saveFormDataToLocalStorage();
        }
    }

    handleEmailStatusUpdate(data: ProtocolEmailStatus): void {
        if (data.appointmentId !== this.data.appointment.uuid) {
            return;
        }

        if (data.status === 'success') {
            this.isProcessingPdf = false;

            this.data.appointment.protocolPdfSentToClient = true;
            this.data.appointment.protocolPdfSentOn = new Date();

            this.toastService.add({
                severity: 'success',
                title: this.translateService.instant('COMPLAINT_APPOINTMENT|Wiadomość została zakolejkowana do wysłania'),
                body: this.translateService.instant('COMPLAINT_APPOINTMENT|Protokół został wysłany na adres e-mail klienta'),
            });
        } else if (data.status === 'failed' || data.status === 'error') {
            this.isProcessingPdf = false;

            this.toastService.add({
                severity: 'error',
                title: this.translateService.instant('COMPLAINT_APPOINTMENT|Wystąpił błąd podczas wysyłania protokołu'),
                body: this.translateService.instant('COMPLAINT_APPOINTMENT|Spróbuj ponownie później'),
            });
        }
    }

    async signDelivery() {
        this.signMode = true;
        if (this.mobileView) {
            if (this.signMode) {
                setTimeout(() => {
                    this.initPad();
                }, 0);
            };
        } else {
            setTimeout(() => {
                this.initPad();
            }, 0);
        }
    }

    private initPad() {
        const canvas = this.signaturePadElement.nativeElement;
        if (this.isHorizontal) {
            canvas.width = canvas.offsetWidth;
            canvas.height = canvas.offsetHeight;
        }
        if (this.signMode && this.signaturePadElement && !this.signaturePad) {
            this.signaturePad = new SignaturePad(this.signaturePadElement.nativeElement);
            this.signaturePad.clear();
        }
    }

    async cancelSign() {
        this.signMode = false;
        this.withoutClientSign = false;
        if (this.clientSigned && !this.servicemanSigned) {
            this.clientSigned = false;
        }
        if (this.signaturePad) {
            this.signaturePad.off();
            this.signaturePad = null;
        }
    }

    clearSign() {
        if (this.signaturePad) {
            this.signaturePad.clear();
        }
    }

    isEmptySign() {
        if (this.signaturePad) {
            return this.signaturePad.isEmpty();
        }
        return true;
    }

    async saveSign() {
        if (this.isEmptySign()) {
            return;
        }
        this.saving = true;
        const signature = this.signaturePad.toDataURL();
        if (!this.clientSigned) {
            this.data.appointment.clientSignatureImage = signature;
            this.clientSigned = true;
        } else {
            this.data.appointment.servicemanSignatureImage = signature;
            this.servicemanSigned = true;
        }

        try {
            this.signMode = false;
            if (this.signaturePad) {
                this.signaturePad.off();
                this.signaturePad = null;
            }
            this.saving = false;

            this.save();
            localStorage.removeItem('formData');
            if (!this.servicemanSigned) {
                this.checkOrientation();
                await this.signDelivery();
            }
            setTimeout(() => {
                this.sign$.next();
            }, 0);
        } catch (e) {
            console.error(e);
        }
    }

    async proceedWithoutClientSign() {
        this.clientSigned = true;
        this.withoutClientSign = true;
        this.signDelivery();
    }

    addAttachmentToAppointment() {
        this.loadAttachments();
    }

    removeAttachmentToAppointment() {
        this.loadAttachments();
    }

    checkOrientation() {
        window.screen.orientation.type.includes('landscape') ? this.isHorizontal = true : this.isHorizontal = false;
        if (this.signaturePadElement) {
            this.initPad();
        }
    }

    async save() {
        if (this.clientSigned && !this.servicemanSigned) {
            this.complaintService.saveProtocolSignature(this.data.appointment, 'client');
        } else if (this.clientSigned && this.servicemanSigned) {
            this.data.appointment.protocolDate = new Date();
            this.complaintService.saveProtocolSignature(this.data.appointment, 'serviceman');
            this.complaintService.saveProtocolFields(this.data.appointment, this.data.attachments);
            this.data.editMode = false;
        }
    }

    proceedToClientView() {
        this.data.clientView = true;
    }

    backToEditMode() {
        if (this.withoutClientSign) {
            this.clientSigned = false;
            this.withoutClientSign = false;
            this.data.clientView = true;
        }
        this.data.clientView = false;
    }

    printAppointmentProtocolPdf() {
        const appointment = this.data.appointment;
        if (appointment) {
            window.open(
                `${window.location.origin}/api/pdf/appointment-protocol/${appointment.uuid}`,
                '_blank'
            );
        }
    }

    confirmSendingProtocol() {
        const appointment = this.data.appointment;
        if (appointment) {
            this.complaintService.confirmSendingProtocol(this.data.appointment);
        }
    }

    scrollToTarget(el: HTMLElement) {
        el.scrollIntoView({ behavior: 'smooth' });
    }

    private saveFormDataToLocalStorage() {
        const formData = {
            appointment: this.data.appointment,
            editMode: this.data.editMode,
            clientView: this.data.clientView,
            clientSigned: this.clientSigned,
            servicemanSigned: this.servicemanSigned,
            withoutClientSign: this.withoutClientSign
        };
        localStorage.setItem(`formData-${this.data.appointment.uuid}`, JSON.stringify(formData));
    }

    private retrieveFormDataFromLocalStorage() {
        const formData = localStorage.getItem(`formData-${this.data.appointment.uuid}`);
        if (formData) {
            const parsedData = JSON.parse(formData);
            this.data.appointment = parsedData.appointment;
            this.data.editMode = parsedData.editMode;
            this.data.clientView = parsedData.clientView;
            this.clientSigned = parsedData.clientSigned;
            this.servicemanSigned = parsedData.servicemanSigned;
            this.withoutClientSign = parsedData.withoutClientSign;
        }
    }

    close() {
        this.complaintService.setIsProcessingPdf(false);
        this.dialogRef.close();
    }

    private loadAttachments() {
        this.data.attachments = this.complaintAppointmentService.getAttachments(this.data.appointment.uuid);
    }

    triggerWorkflowAfterSignSynced() {
        if (!this.data.editMode && this.data.clientView && this.data.appointment.servicemanSignatureImage) {
            let protocolStatus: AppointmentStatus = 'completed-pending';
            if (this.data.appointment.workStatus === 'completed') {
                protocolStatus = 'completed';
            } else if (this.data.appointment.workStatus === 'inProgress') {
                protocolStatus = 'completed-pending';
            } else if (this.data.appointment.workStatus === 'rejected') {
                protocolStatus = 'rejected';
            }
            this.complaintService.triggerWorkflow('protocol_confirmed', this.data.complaint, {
                protocolStatus,
                level: this.data.userType,
            }, this.data.positionsWorkflowsGroups);
        }
    }
}
